/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { motion } from 'motion/react';
import { X, Home, Phone, MapPin, FileText, Trash2, ImagePlus, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import domacinstvoService from '../api/domacinstvoService';
import { useMunicipalities } from './MunicipalitiesContext';
import { useToast } from './Toast';
import EditableField from './EditableField';
import Modal from './Modal';
import { filterByPrefix } from '../utils/selectStyles';

const MAX_IMAGES = 7;
const MAX_IMAGE_BYTES = 9 * 1024 * 1024;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

const readDomacinstvoIdFromStorage = () => {
  try {
    const saved = localStorage.getItem('user');
    const parsed = saved ? JSON.parse(saved) : null;
    if (!parsed) return null;
    const d = parsed.domacinstvoId;
    if (!d) return null;
    return typeof d === 'object' ? d._id || d.id || null : d;
  } catch {
    return null;
  }
};

export default function HouseholdModal({ isOpen, onClose }) {
  const toast = useToast();
  const { names: gradovi } = useMunicipalities();
  const gradoviOptions = useMemo(() => gradovi.map((g) => ({ value: g, label: g })), [gradovi]);

  const [domacinstvoId, setDomacinstvoId] = useState(null);
  const [domacinstvo, setDomacinstvo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Image staging state
  const [existingImages, setExistingImages] = useState([]); // {_id?, url}[]
  const [newImages, setNewImages] = useState([]);           // File[]
  const [newPreviews, setNewPreviews] = useState([]);       // blob URL[]
  const [isSavingImages, setIsSavingImages] = useState(false);
  const fileInputRef = useRef(null);

  const initialExistingRef = useRef([]); // snapshot to detect changes

  useEffect(() => {
    if (!isOpen) return;
    const id = readDomacinstvoIdFromStorage();
    setDomacinstvoId(id);
    setError('');

    if (!id) {
      setDomacinstvo(null);
      setExistingImages([]);
      initialExistingRef.current = [];
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    domacinstvoService.getById(id)
      .then((res) => {
        if (cancelled) return;
        const d = res.data;
        setDomacinstvo(d);
        const imgs = Array.isArray(d?.images) ? d.images.map((i) => ({ _id: i._id, url: i.url })) : [];
        setExistingImages(imgs);
        initialExistingRef.current = imgs;
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Ne mogu da učitam podatke domaćinstva.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Reset staged new images on close
  useEffect(() => {
    if (isOpen) return;
    newPreviews.forEach((url) => URL.revokeObjectURL(url));
    setNewImages([]);
    setNewPreviews([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    return () => {
      newPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalImages = existingImages.length + newImages.length;

  const hasImageChanges = useMemo(() => {
    if (newImages.length > 0) return true;
    const before = initialExistingRef.current;
    if (before.length !== existingImages.length) return true;
    const beforeIds = new Set(before.map((i) => i._id || i.url));
    return existingImages.some((i) => !beforeIds.has(i._id || i.url));
  }, [existingImages, newImages]);

  const handlePickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    const remaining = MAX_IMAGES - totalImages;
    const accepted = [];
    const rejected = [];
    for (const file of files.slice(0, remaining)) {
      if (!ALLOWED_MIME.includes(file.type)) {
        rejected.push(`${file.name}: nepodržan format`);
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        rejected.push(`${file.name}: veća od 9 MB`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length > 0) {
      setNewImages((prev) => [...prev, ...accepted]);
      setNewPreviews((prev) => [...prev, ...accepted.map((f) => URL.createObjectURL(f))]);
      setError('');
    }
    if (rejected.length > 0) setError(rejected.join(', '));
  };

  const removeExisting = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNew = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
    setNewPreviews((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const saveImages = async () => {
    if (!domacinstvoId || isSavingImages || !hasImageChanges) return;
    setIsSavingImages(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('imagesEdited', '1');
      existingImages.forEach((img) => fd.append('keepImages[]', img._id || img.url));
      newImages.forEach((file) => fd.append('images', file));
      const res = await domacinstvoService.update(domacinstvoId, fd);
      const fresh = res.data;
      setDomacinstvo(fresh);
      const imgs = Array.isArray(fresh?.images) ? fresh.images.map((i) => ({ _id: i._id, url: i.url })) : [];
      setExistingImages(imgs);
      initialExistingRef.current = imgs;
      newPreviews.forEach((url) => URL.revokeObjectURL(url));
      setNewImages([]);
      setNewPreviews([]);
      toast.success('Slike sačuvane.');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju slika.';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSavingImages(false);
    }
  };

  const saveField = async (field, value, errorPrefix, successLabel) => {
    if (!domacinstvoId) {
      setError(`Nedostaje domaćinstvo za izmenu ${errorPrefix}.`);
      throw new Error('no domacinstvo');
    }
    if ((field === 'name' || field === 'phone' || field === 'municipality') && !value) {
      setError(`${errorPrefix} ne može biti prazan.`);
      throw new Error('empty');
    }
    setError('');
    try {
      const res = await domacinstvoService.update(domacinstvoId, { [field]: value });
      const fresh = res.data;
      setDomacinstvo(fresh);
      // Update localStorage so other screens see fresh data
      try {
        const saved = localStorage.getItem('user');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed?.domacinstvoId && typeof parsed.domacinstvoId === 'object') {
            parsed.domacinstvoId = { ...parsed.domacinstvoId, [field]: fresh?.[field] ?? value };
            localStorage.setItem('user', JSON.stringify(parsed));
            window.dispatchEvent(new Event('auth-changed'));
          }
        }
      } catch { /* noop */ }
      toast.success(`${successLabel} sačuvan${successLabel.endsWith('a') ? 'a' : ''}.`);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Greška pri izmeni.';
      setError(msg);
      toast.error(msg);
      throw err;
    }
  };

  const householdName = domacinstvo?.name || '';
  const phone = domacinstvo?.phone || '';
  const municipality = domacinstvo?.municipality || '';
  const description = domacinstvo?.description || '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      label="Moje domaćinstvo"
      panelClassName="w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl overflow-hidden"
    >
      <button
        onClick={onClose}
        aria-label="Zatvori"
        className="absolute top-6 right-6 p-2 text-sage-400 hover:text-sage-600 hover:bg-sage-50 hover:scale-110 rounded-full transition-all z-10"
      >
        <X size={20} />
      </button>
            <div className="p-8 sm:p-10 max-h-[88dvh] overflow-y-auto custom-scrollbar">

              <div className="flex flex-col items-center mb-6">
                <div className="w-20 h-20 rounded-3xl bg-sage-100 text-sage-700 flex items-center justify-center mb-4 shadow-sm">
                  <Home size={32} />
                </div>
                <h2 className="text-2xl font-serif font-bold text-earth-900 text-center">
                  {householdName || 'Moje Domaćinstvo'}
                </h2>
                {municipality && <p className="text-sage-600 text-sm">{municipality}</p>}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-2xl px-4 py-3 mb-4">
                  {error}
                </div>
              )}

              {isLoading && !domacinstvo && (
                <p className="text-center text-sage-500 text-sm py-6">Učitavanje...</p>
              )}

              {!isLoading && !domacinstvoId && (
                <p className="text-center text-sage-500 text-sm py-6">
                  Nemate povezano domaćinstvo.
                </p>
              )}

              {domacinstvoId && (
                <>
                  <div className="space-y-3">
                    <EditableField
                      icon={<Home size={16} />}
                      label="Naziv gazdinstva"
                      value={householdName}
                      placeholder="Naziv gazdinstva"
                      onSave={(v) => saveField('name', v, 'naziv', 'Naziv')}
                    />
                    <EditableField
                      icon={<Phone size={16} />}
                      label="Telefon"
                      value={phone}
                      inputType="tel"
                      placeholder="npr. +381 60 123 4567"
                      onSave={(v) => saveField('phone', v, 'telefon', 'Telefon')}
                    />
                    <EditableField
                      icon={<MapPin size={16} />}
                      label="Opština"
                      value={municipality}
                      onSave={(v) => saveField('municipality', v, 'opštinu', 'Opština')}
                      renderEdit={({ value, setValue, isSaving }) => (
                        <Select
                          autoFocus
                          options={gradoviOptions}
                          value={gradoviOptions.find((o) => o.value === value) || null}
                          onChange={(opt) => setValue(opt?.value || '')}
                          placeholder="Izaberite opštinu"
                          filterOption={filterByPrefix}
                          isDisabled={isSaving}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          styles={{
                            control: (base) => ({ ...base, minHeight: 32, fontSize: 14, fontWeight: 700 }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                      )}
                    />
                    <EditableField
                      icon={<FileText size={16} />}
                      label="Opis"
                      value={description}
                      placeholder="Kratak opis vašeg gazdinstva..."
                      onSave={(v) => saveField('description', v, 'opis', 'Opis')}
                      renderEdit={({ value, setValue, save, cancel, isSaving }) => (
                        <textarea
                          autoFocus
                          value={value}
                          onChange={(e) => setValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save();
                            if (e.key === 'Escape') cancel();
                          }}
                          disabled={isSaving}
                          rows={3}
                          placeholder="Kratak opis vašeg gazdinstva..."
                          className="w-full bg-white border border-sage-200 rounded-lg px-2 py-1.5 text-sm text-earth-900 focus:ring-2 focus:ring-sage-300 outline-none transition-all resize-y disabled:opacity-60"
                        />
                      )}
                    />
                  </div>

                  <div className="mt-6 pt-6 border-t border-sage-100">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-bold text-earth-900">
                        Slike <span className="text-sage-400 font-normal">({totalImages}/{MAX_IMAGES})</span>
                      </label>
                      {hasImageChanges && (
                        <motion.button
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          type="button"
                          onClick={saveImages}
                          disabled={isSavingImages}
                          className="px-4 py-1.5 bg-sage-600 text-white rounded-xl text-xs font-bold flex items-center gap-2 hover:bg-sage-700 transition-colors disabled:opacity-60"
                        >
                          {isSavingImages && <Loader2 size={12} className="animate-spin" />}
                          {isSavingImages ? 'Čuvanje...' : 'Sačuvaj izmene'}
                        </motion.button>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      {existingImages.map((img, idx) => (
                        <div key={img._id || img.url} className="relative aspect-square rounded-xl overflow-hidden group border border-sage-100 shadow-sm">
                          <img src={img.url} alt={`Slika ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeExisting(idx)}
                            aria-label="Ukloni sliku"
                            className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-md opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                      {newPreviews.map((src, idx) => (
                        <div key={`new-${idx}`} className="relative aspect-square rounded-xl overflow-hidden group border-2 border-sage-300 shadow-sm">
                          <img src={src} alt={`Nova slika ${idx + 1}`} className="w-full h-full object-cover" />
                          <span className="absolute bottom-0 left-0 right-0 bg-sage-600/90 text-white text-[11px] py-0.5 text-center font-bold">
                            Nova
                          </span>
                          <button
                            type="button"
                            onClick={() => removeNew(idx)}
                            aria-label="Ukloni novu sliku"
                            className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-md opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                      {totalImages < MAX_IMAGES && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="aspect-square bg-sage-50 border-2 border-dashed border-sage-200 rounded-xl flex flex-col items-center justify-center gap-1 hover:bg-sage-100 hover:border-sage-300 transition-all text-sage-600"
                        >
                          <ImagePlus size={20} />
                          <span className="text-[11px] font-bold">Dodaj sliku</span>
                        </button>
                      )}
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handlePickFiles}
                      multiple
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                    />
                    <p className="mt-2 text-xs text-sage-500 italic">JPG, PNG ili WEBP. Do 9 MB po slici.</p>
                  </div>
                </>
              )}

              <button
                onClick={onClose}
                className="w-full mt-8 py-4 bg-sage-600 text-white rounded-2xl font-bold hover:bg-sage-700 hover:shadow-xl hover:shadow-sage-700/30 transition-all"
              >
                Zatvori
              </button>
            </div>
    </Modal>
  );
}
